import { FC, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { t, Trans } from "@lingui/macro";
import { Heading, Text } from "@otrium/atoms";
import { omit } from "lodash-es";
import qs from "query-string";
import { Iso2LetterLocale } from "src/lib/Intl";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { updateSession } from "src/utils/updateSession";
import { CheckoutStepPaymentSkeleton } from "src/molecules/CheckoutStepPaymentSkeleton";
import { CheckoutOrderSummary } from "src/molecules/CheckoutOrderSummary";
import { useLingui } from "@lingui/react";
import MolliePayment from "./MolliePayment";
import { useMollie } from "src/hooks/useMollie";
import { isNativeApp } from "src/utils/isNativeApp";
import { Cart } from "src/types/ctgraphql.d";
import {
  getIsCheckoutValid,
  getIsShippingMethodValid,
} from "src/utils/validationSchema";
import { useOptIn } from "src/hooks/useOptIn";
import { useRecalculateCart } from "src/hooks/useRecalculateCart";
import { useSegment } from "src/hooks/useSegment";
import { Miscellaneous, Origin } from "src/types/graphql.d";
import {
  usePspPaymentCreateTransaction,
  PspPaymentCreateTransactionData,
} from "src/hooks/usePspPaymentCreateTransaction";
import { usePspMollieEnabled } from "src/hooks/usePspMollieEnabled";
import { MollieCardComponentResult } from "src/types/psp";
import {
  buildCheckoutTrackingData,
  getMobilePaymentUrl,
  rememberCheckoutOptIn,
} from "./utils";
import { ID } from "@ht-sdks/events-sdk-js-browser";
import { SummaryWrapper } from "../CartView/CartView.styled";
import { getCartInfo } from "src/utils/ct/cart-helper";
import { usePageViewedContext } from "src/hooks/context/use-page-viewed-context";
import { setStorageJSON } from "src/utils/helper/localStorage";
import { OT_CHECKOUT_TRACKING_DATA } from "src/utils/constants";
import { storeConfig } from "src/config/app-config";

const getCheckout = (cart: Cart) => ({
  firstName: cart.shippingAddress?.firstName || "",
  lastName: cart.shippingAddress?.lastName || "",
  email: cart.shippingAddress?.email || "",
  phone: cart.shippingAddress?.phone || "",
  shippingAddress: {
    streetName: cart.shippingAddress?.streetName || "",
    streetNumber: cart.shippingAddress?.streetNumber || "",
    additionalStreetInfo: cart.shippingAddress?.additionalStreetInfo || "",
    postalCode: cart.shippingAddress?.postalCode || "",
    city: cart.shippingAddress?.city || "",
    state: cart.shippingAddress?.state || "",
    country: (cart.shippingAddress?.country as Iso2LetterLocale) || "",
    isStreetNumberNotRequired: cart.shippingAddress?.streetNumber === "",
  },
  billingAddress: {
    streetName: cart.billingAddress?.streetName || "",
    streetNumber: cart.billingAddress?.streetNumber || "",
    additionalStreetInfo: cart.billingAddress?.additionalStreetInfo || "",
    postalCode: cart.billingAddress?.postalCode || "",
    city: cart.billingAddress?.city || "",
    state: cart.shippingAddress?.state || "",
    country: (cart.billingAddress?.country as Iso2LetterLocale) || "",
    isStreetNumberNotRequired: cart.billingAddress?.streetNumber === "",
  },
});

const CheckoutStepPayment: FC<{
  cart: Cart | null;
  cartLoading: boolean;
  miscellaneous: Miscellaneous | undefined;
  setShowWarning: (showWarning: boolean) => void;
  setWarningMessage: (warningMessage: string) => void;
  onNextStep: ({ isPaymentPending }: { isPaymentPending: boolean }) => void;
}> = ({
  cart,
  cartLoading,
  miscellaneous,
  setShowWarning,
  setWarningMessage,
  onNextStep,
}) => {
  const { data: session } = useSession();
  const signInPlatform = session?.user.signInPlatform;

  const router = useRouter();
  const { i18n } = useLingui();

  // Adyen related params
  const sessionId = router.query.sessionId as string | undefined;
  const redirectResult = router.query.redirectResult as string | undefined;

  // Mollie related params
  const paymentId = router.query.payment_id as string | undefined;

  const paymentIsInProgress = !!((sessionId && redirectResult) || paymentId);

  const [isPayPalSelected, setIsPayPalSelected] = useState<boolean>(false);
  const [isApplePaySelected, setIsApplePaySelected] = useState<boolean>(false);
  const [isCheckoutValid, setIsCheckoutValid] = useState<boolean>();

  const [pspPaymentIsLoading, setPspPaymentIsLoading] =
    useState<boolean>(false);
  const [pspPaymentId, setPspPaymentId] = useState<string>();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>();
  const selectedPaymentMethodRef = useRef<string>();
  const [anonymousId, setAnonymousId] = useState<ID>("");

  const isShippingMethodValid = getIsShippingMethodValid(cart);
  const isMollieEnabled = usePspMollieEnabled();

  const payPalRef = useRef<HTMLDivElement>(null);
  const cartInfo = useMemo(() => getCartInfo(cart), [cart]);
  const { pageViewTracked } = usePageViewedContext();

  const {
    checkoutSecondStepStarted,
    segmentSecondCheckoutStepCompleted,
    segmentOptIn,
    user,
  } = useSegment();

  const { optIn } = useOptIn({
    onCompleted: (data) => {
      if (data.optIn?.token && signInPlatform !== AuthProvider.GUEST_LOGIN) {
        void updateSession({ token: data.optIn.token });
      }
    },
  });

  const showFailedPaymentWarning = useCallback(() => {
    setShowWarning(true);
    setWarningMessage(
      t(i18n)`Unfortunately your payment has failed. Please try again.`
    );

    setPspPaymentIsLoading(false);
  }, [setShowWarning, setWarningMessage, i18n]);

  // PSP / Mollie related hook
  const { pspPaymentCreateTransaction } = usePspPaymentCreateTransaction({
    onCompleted: (data: PspPaymentCreateTransactionData) => {
      if (!data.pspPaymentCreateTransaction?.checkout_url) {
        showFailedPaymentWarning();
        return;
      }

      void router.push(data.pspPaymentCreateTransaction.checkout_url);
    },

    onError: () => {
      showFailedPaymentWarning();
    },
  });

  // Init Mollie Card Components (only when Mollie is enabled)
  const { mollie: mollieCardComponent } = useMollie() as {
    mollie: any | undefined;
  };

  const { loading: isCartRecalculating, recalculateCart } = useRecalculateCart({
    onError: () => {
      void router.push({
        pathname: "/checkout",
        query: { ...router.query, step: 1 },
      });
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    const htUser = user;
    setAnonymousId(htUser?.anonymousId?.() || "");
  }, [user]);

  useEffect(() => {
    window.scrollTo({
      behavior: "smooth",
      top: 0,
    });
  }, []);

  const isCartRecalculatedRef = useRef(false);

  useEffect(() => {
    if (
      !cart ||
      isCartRecalculating ||
      paymentIsInProgress ||
      isCartRecalculatedRef.current
    ) {
      return;
    }

    isCartRecalculatedRef.current = true;

    void recalculateCart(cartInfo);
  }, [
    cart,
    cartInfo,
    isCartRecalculating,
    paymentIsInProgress,
    recalculateCart,
  ]);

  useEffect(() => {
    if (cart && !cartLoading && !paymentIsInProgress) {
      setIsCheckoutValid(
        getIsCheckoutValid(getCheckout(cart)) && isShippingMethodValid
      );
    }
  }, [
    cart,
    cartLoading,
    paymentIsInProgress,
    router,
    isShippingMethodValid,
    isCartRecalculating,
  ]);

  const checkoutViewedRef = useRef(false);
  useEffect(() => {
    if (
      !isCheckoutValid ||
      !cart ||
      cartLoading ||
      isCartRecalculating ||
      paymentIsInProgress ||
      checkoutViewedRef.current ||
      !pageViewTracked
    ) {
      return;
    }

    void checkoutSecondStepStarted(cart);

    checkoutViewedRef.current = true;
  }, [
    cart,
    isCheckoutValid,
    cartLoading,
    pageViewTracked,
    isCartRecalculating,
    paymentIsInProgress,
    checkoutSecondStepStarted,
  ]);

  useEffect(() => {
    if (isCheckoutValid === false) {
      void router.push({
        pathname: "/checkout",
        query: { ...router.query, step: 1 },
      });
    }
  }, [isCheckoutValid, router]);

  const triggerTrackStepComplete = useCallback(
    (data?: { fundingSource: string }) => {
      if (cart) {
        void segmentSecondCheckoutStepCompleted(
          cart.id,
          selectedPaymentMethod ?? data?.fundingSource
        );
      }
    },
    [cart, selectedPaymentMethod, segmentSecondCheckoutStepCompleted]
  );

  const completeMolliePayment = useCallback(
    (
      optInShown: boolean,
      isOptIn: boolean,
      cardToken?: string,
      applePayToken?: string
    ) => {
      if (!pspPaymentId || !cart || !selectedPaymentMethod) {
        return;
      }

      setShowWarning(false);
      setPspPaymentIsLoading(true);

      if (optInShown && cart?.shippingAddress?.email) {
        if (isOptIn) {
          void optIn({
            email: cart.shippingAddress.email,
            segment_anonymous_id: anonymousId,
            metadata: {
              origin: Origin.Checkout,
            },
          });

          rememberCheckoutOptIn();
        }

        void segmentOptIn(!!isOptIn, cart?.shippingAddress?.email, "checkout");
      }

      const { url, query } = qs.parseUrl(window.location.href);

      let paymentUrl = url;
      // If the selected payment method is iDEAL and in the native app, we need to change the redirect url containing the mobile schema.
      if (isNativeApp && selectedPaymentMethod === "ideal") {
        paymentUrl = getMobilePaymentUrl();
      }

      query.payment_id = pspPaymentId;

      void pspPaymentCreateTransaction({
        payment_id: pspPaymentId,
        cart_id: cart?.id,
        return_url: qs.stringifyUrl({
          url: paymentUrl,
          query: {
            ...query,
            step: "2",
          },
        }),
        payment_method: selectedPaymentMethod,
        card_token: cardToken ?? undefined,
        apple_pay_token: applePayToken ?? undefined,
      });
    },
    [
      optIn,
      cart,
      selectedPaymentMethod,
      pspPaymentId,
      anonymousId,
      segmentOptIn,
      pspPaymentCreateTransaction,
      setShowWarning,
    ]
  );

  const handleClickPay = useCallback(
    (optInShown: boolean, isOptIn: boolean, applePayToken?: string) => {
      if (cart && pspPaymentId && selectedPaymentMethod) {
        const trackingData = buildCheckoutTrackingData(
          cart,
          pspPaymentId,
          selectedPaymentMethod,
          storeConfig
        );
        setStorageJSON(OT_CHECKOUT_TRACKING_DATA, trackingData);
      }

      if (
        isMollieEnabled &&
        selectedPaymentMethod &&
        pspPaymentId &&
        cart &&
        mollieCardComponent
      ) {
        switch (selectedPaymentMethod) {
          case "applepay":
            completeMolliePayment(
              optInShown,
              isOptIn,
              undefined,
              applePayToken
            );
            break;
          case "creditcard":
            mollieCardComponent
              .createToken()
              .then((result: MollieCardComponentResult) => {
                if (result.error) {
                  showFailedPaymentWarning();
                  return;
                }

                completeMolliePayment(optInShown, isOptIn, result.token);
              });
            break;
          default:
            completeMolliePayment(optInShown, isOptIn);
            break;
        }
      }

      triggerTrackStepComplete();
    },
    [
      isMollieEnabled,
      selectedPaymentMethod,
      pspPaymentId,
      cart,
      triggerTrackStepComplete,
      completeMolliePayment,
      mollieCardComponent,
      showFailedPaymentWarning,
    ]
  );

  const onPaymentInitiated = (paymentId: string) => {
    setPspPaymentId(paymentId);
  };

  const onPaymentCompleted = useCallback(
    ({ isPaymentPending }: { isPaymentPending: boolean }) => {
      onNextStep({ isPaymentPending: !!isPaymentPending });
    },
    [onNextStep]
  );

  const onPaymentRefused = () => {
    if (isMollieEnabled) {
      const query = qs.parse(window.location.search);
      void router.push({
        pathname: "/checkout",
        query: omit(query, ["payment_id"]),
      });

      showFailedPaymentWarning();
    } else {
      if (paymentIsInProgress) {
        const query = qs.parse(window.location.search);
        void router.push({
          pathname: "/checkout",
          query: omit(query, [
            "sessionId",
            "redirectResult",
            "resultCode",
            "type",
          ]),
        });
      } else {
        setIsPayPalSelected(false);
      }
      setShowWarning(true);
      setWarningMessage(
        "Unfortunately your payment has failed. Please try again."
      );
    }
  };

  const onPaymentCancelled = () => {};

  if ((!isCheckoutValid || isCartRecalculating) && !paymentIsInProgress) {
    return <CheckoutStepPaymentSkeleton />;
  }

  return (
    <>
      {paymentIsInProgress && (
        <section>
          <Heading as="h1" variant="h1">
            <Trans>Your payment is being processed</Trans>
          </Heading>
          <Text as="h4" variant="h4" fontFamily="CharisSILW" mt={4}>
            <Trans>
              It will take a few seconds.
              <br />
              Please do not leave or refresh this page.
            </Trans>
          </Text>
        </section>
      )}

      <section>
        <MolliePayment
          title={
            <Heading as="h4" variant="h4">
              <Trans>Payment method</Trans>
            </Heading>
          }
          cartInfo={cartInfo}
          paymentId={paymentId}
          onPaymentInitiated={onPaymentInitiated}
          paymentIsInProgress={paymentIsInProgress}
          onPaymentMethodChanged={(paymentMethod) => {
            setPspPaymentIsLoading(false);
            setIsApplePaySelected(paymentMethod === "applepay");
            setSelectedPaymentMethod(paymentMethod);
            selectedPaymentMethodRef.current = paymentMethod;
          }}
          onPaymentCompleted={onPaymentCompleted}
          onPaymentRefused={onPaymentRefused}
          onPaymentCancelled={onPaymentCancelled}
        />
      </section>

      {!paymentIsInProgress && (
        <SummaryWrapper>
          <CheckoutOrderSummary
            cart={cart}
            miscellaneous={miscellaneous}
            step={2}
            buttonLabel={t(i18n)`Order & Pay`}
            buttonLoading={pspPaymentIsLoading}
            onClick={handleClickPay}
            payPalRef={payPalRef}
            isPayPalSelected={isPayPalSelected}
            isApplePaySelected={isApplePaySelected}
            paymentId={pspPaymentId}
            loading={cartLoading}
          />
        </SummaryWrapper>
      )}
    </>
  );
};

export default CheckoutStepPayment;
